import React, { useContext, useEffect, useRef } from 'react'
import styled from 'styled-components'

import { ColorFormat, UserPreferences, Layer } from 'modules/shares/types'
import { InspectorData, InspectorState } from 'modules/shares/Inspector/types'
import { VersionFragment } from '@sketch/gql-types'

import InspectorError from './InspectorError'
import ArtboardInfo from '../ArtboardInfo'
import LayerInfo from '../LayerInfo'
import InspectorSkeleton from './components/InspectorSkeleton'
import IntroductionModal from './components/IntroductionModal'
import { useLocalStorage } from 'react-use'
import { AttributeList } from '../Attribute'

import { getSymbolDocumentName } from 'modules/shares/Inspector/Sidebar/utils'
import { useForTablet, ErrorBoundary, ModalContext } from '@sketch/components'

const InspectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  height: 100%;

  ${AttributeList} {
    width: 100%;
  }
`

export interface InspectorProps {
  inspector: InspectorState
  className?: string
  error?: string
  errorReason?: string
  isPage?: boolean
  isSymbol?: boolean
  artboardName: string
  layer?: Layer
  ready: boolean
  userPreferences?: UserPreferences
  shareIdentifier: string
  currentVersion: VersionFragment
  permanentArtboardShortId?: string
}

// Helpers
function checkArtboardExportableAssets(
  artboard: InspectorData | undefined
): boolean {
  if (!artboard || !artboard.layers) {
    return false
  }

  return Object.values(artboard.layers).some(
    layer => layer.exportFormats?.length
  )
}

const Inspector: React.FC<InspectorProps> = ({
  inspector,
  className,
  error,
  errorReason,
  isPage,
  isSymbol,
  artboardName,
  layer,
  ready,
  userPreferences,
  shareIdentifier,
  currentVersion,
  permanentArtboardShortId,
}) => {
  const isTabletAndBigger = useForTablet()
  const hasArtboardExportableAssets = useRef<null | boolean>(null)

  // In the future, UserPreferences attributes must come from graphql
  // Using localStorage is a temporary solution
  // Check https://github.com/sketch-hq/Cloud/issues/2914
  const [colorFormat, setColorFormat] = useLocalStorage(
    'inspector_color_format',
    userPreferences && userPreferences.colorFormat
      ? userPreferences.colorFormat
      : ColorFormat.HEX
  )
  const { showModal } = useContext(ModalContext)
  const [introductionSeen, setIntroductionSeen] = useLocalStorage(
    'inspector_introduction_seen',
    false
  )

  useEffect(() => {
    if (!introductionSeen && isTabletAndBigger) {
      // Because of how Tabs are composed (Tab and TabPanel being at the same
      // level), this side effect is triggered before the URL change that
      // happens when clicking a <Tab /> (in this case #inspector), using
      // `setTimeout` allows to delay this execution until next tick
      setTimeout(() => {
        showModal(IntroductionModal)
        setIntroductionSeen(true)
      }, 0)
    }
  }, [showModal, introductionSeen, setIntroductionSeen, isTabletAndBigger])

  // We are checking if the arboard as any exportable assets
  // and keeping it in a ref to keep track of this value
  // even if we select layers / arboards afterwards
  useEffect(() => {
    hasArtboardExportableAssets.current = checkArtboardExportableAssets(
      inspector.data
    )
  }, [inspector.data])

  if (error) {
    return <InspectorError error={error} reason={errorReason} />
  }

  if (!ready) {
    return <InspectorSkeleton isPage={isPage} />
  }

  const symbolDocumentName = getSymbolDocumentName(inspector)

  return (
    <ErrorBoundary>
      <InspectorContainer className={className}>
        {layer ? (
          <LayerInfo
            symbolInfo={inspector.symbol}
            layer={layer}
            onColorFormatChange={setColorFormat}
            colorFormat={colorFormat!}
            shareIdentifier={shareIdentifier}
            currentVersion={currentVersion}
            hasArtboardExportableAssets={!!hasArtboardExportableAssets.current}
            symbolDocumentName={symbolDocumentName}
            textStyles={inspector?.data?.textStyles}
            layerStyles={inspector?.data?.layerStyles}
            permanentArtboardShortId={permanentArtboardShortId}
          />
        ) : (
          <ArtboardInfo
            artboardName={artboardName}
            inspectorData={inspector.data!}
            isSymbol={isSymbol}
            symbolDocumentName={symbolDocumentName}
            onColorFormatChange={setColorFormat}
            colorFormat={colorFormat!}
            currentVersion={currentVersion}
            hasArtboardExportableAssets={!!hasArtboardExportableAssets.current}
          />
        )}
      </InspectorContainer>
    </ErrorBoundary>
  )
}

const InspectorStyled = styled(Inspector)`
  margin: 0;
`

export default InspectorStyled
